/**
 * Codec del protocolo UART de Educabot (ProtocoloEDU 3.1.0, "Protocolo.txt" del
 * repo de trazabilidad).
 *
 * Sin estado de puerto: acá solo se arman, se parsean y se separan tramas. Quien
 * las manda y espera respuesta es eduProtocolClient; quien es dueño del puerto es
 * serialSession. Qué módulo hay detrás de una lectura lo decide moduleTable.
 *
 * La trama es binaria (§4):
 *
 *   0xAA 0xF5 | TIPO | ID_HI ID_LO | CMD | LEN | DATO[LEN] | CHK
 *
 * TIPO y CMD son un byte, los números van big-endian en su tamaño natural y los
 * campos de identidad (SN, SerialString, label, versión) viajan como ASCII. No
 * hay marca de cierre: el largo lo da LEN.
 */

export const MAGIC0 = 0xAA;
export const MAGIC1 = 0xF5;
export const MAX_DATA = 48;
// MAGIC (2) + TIPO + ID (2) + CMD + LEN + CHK = 8 bytes fijos.
export const MAX_FRAME = 8 + MAX_DATA;
export const EVENT_ID = 0x0000;

// 9600 8N1. El firmware lo fija y no hay comando para cambiarlo, así que este es
// el único baudrate al que las tramas significan algo.
export const PROTO_BAUD = 9600;

/**
 * Cuánto esperamos una respuesta.
 *
 * La sección 8 del protocolo dice 500 ms para una respuesta simple y 2000 ms para
 * un bloque, y está bien: es lo que tarda el firmware en contestar cuando lo dejan
 * correr. Acá se espera muchísimo más, y no por desconfianza del firmware.
 *
 * La librería comparte el loop con el código del usuario: atiende el protocolo
 * cuando le toca el turno, y no le toca mientras el sketch está adentro de un
 * delay(). Esto es una plataforma para aprender a programar, donde el delay largo
 * es de las primeras cosas que uno escribe -- y donde el que lo escribe no tiene
 * por qué enterarse de que por eso su placa dejó de reconocerse.
 *
 * Un pedido que caduca antes de que el loop vuelva a atender no dice nada de la
 * placa: dice que llegamos temprano. Y el precio de esperar de más lo paga solo
 * la placa que no contesta, mientras que el de esperar de menos lo paga la que sí.
 */
export const TIMEOUT_MS = 6000;
export const TIMEOUT_BLOCK_MS = 8000;
/**
 * Lo que se le da a una escritura de EEPROM (STSS) antes de darla por perdida.
 *
 * Más que los 500 ms de una lectura porque no es lo mismo: la placa tiene que
 * grabar el bloque y recalcular su CRC antes de contestar el ACK, y en la
 * M24C32-U eso son unos 5 ms por página. Reintentar no molesta -- grabar dos
 * veces el mismo valor deja la EEPROM igual -- pero cortar antes de tiempo daría
 * por fallada una escritura que sí entró.
 */
export const TIMEOUT_WRITE_MS = 8000;
export const RETRY_MAX = 3;

/**
 * Lo que se manda antes de reintentar (§8): tantos ceros como la trama más
 * larga. Si la placa se quedó con media trama nuestra esperando bytes, estos la
 * completan -- llega con el CHK mal y se descarta -- y en reposo se ignoran,
 * porque 0x00 no es MAGIC0.
 */
export const FLUSH = new Uint8Array(MAX_FRAME);

export const TYPE = {
  REQ: 0,
  RSP: 1,
  EVT: 2,
  ACK: 3,
  ERR: 4,
};

/**
 * Valor de CMD (§5, enum ProtoCmd de protocol.h). Los huecos son comandos
 * retirados o reservados: la placa los contesta con ERR NRDY.
 *
 * Los códigos de error (30-34) también son valores de CMD: viajan en ese campo
 * de las tramas ERR.
 */
export const CMD = {
  GTSN: 0,
  GTSS: 1,
  GTUP: 2,
  GTRS: 3,
  GTRK: 4,
  GTFL: 5,
  GTFC: 6,
  GTFW: 8,
  GTLB: 9,
  GTMD: 10,
  GTMA: 11,
  GTAL: 12,
  STLB: 13,
  STSS: 14,
  ACRS: 16,
  ACFL: 17,
  ACBP: 18,
  EVBT: 19,
  EVFL: 20,
  EVMD: 21,
  EVUP: 22,
  GTVL: 27,
  BADCMD: 30,
  BADCHK: 31,
  BADDAT: 32,
  TIMEOUT: 33,
  NRDY: 34,
};

/**
 * Códigos de la trama ERR (§6.3), por nombre. Es lo que lleva error.code en un
 * ProtocolError, así que es lo que hay que mirar para saber qué contestó la placa.
 */
export const ERR_CODE = {
  // Comando desconocido.
  BADCMD: 'BADCMD',
  // Checksum incorrecto, TIPO fuera de rango o LEN > 48: la trama nos llegó rota
  // a la placa, y vale reintentar.
  BADCHK: 'BADCHK',
  // Dato inválido para el comando: un SS que no mide 8 caracteres, por ejemplo.
  BADDAT: 'BADDAT',
  // No listo: comando reservado, callback no registrado, GTVL donde no se mide.
  NRDY: 'NRDY',
};

const byValue = (table) => Object.keys(table).reduce((names, name) => ({ ...names, [table[name]]: name }), {});
const TYPE_NAMES = byValue(TYPE);
const CMD_NAMES = byValue(CMD);

export const typeName = (type) => TYPE_NAMES[type] || `TIPO${type}`;
export const cmdName = (cmd) => CMD_NAMES[cmd] || `CMD${cmd}`;

// Origen del reset (§6.1), en el orden del enum ResetKind.
export const RESET_KIND = ['PWON', 'XRST', 'WDTO', 'BORF', 'UNKN'];

// Códigos de fault (§6.2), en el orden del enum FaultCode. El 7 (NONE) es "sin
// fault" y solo lo contesta GTFL.
export const FAULT = ['EEW', 'EER', 'I2C', 'SNF', 'WDT', 'OVF', 'USR'];
const FAULT_NONE = 7;

/**
 * Los dos identificadores que guarda la placa, que son distintos y no
 * intercambiables (ver §6.6 del protocolo).
 *
 * El Serial Number (GTSN) es el del hardware: sale de la MAC en ESP32 o del UID
 * de la EEPROM M24C32-U en la placa UNO, y no se puede escribir -- no existe
 * comando para hacerlo. Identifica al chip, no a la unidad vendida.
 *
 * El SerialString (GTSS / STSS) es el número de producción que asigna Educabot:
 * el mismo que graba la herramienta de manufactura y que figura en la planilla de
 * seriales. Es el que viaja como serial_string en los eventos de trazabilidad.
 *
 * Cada uno tiene su valor de "no hay". Importan porque la placa siempre contesta
 * algo: tomar ese relleno por un identificador juntaría en el backend a todas las
 * placas sin grabar bajo una sola.
 */
export const NO_SERIAL_STRING = '--------';
export const NO_SERIAL_NUMBER = '00000000';

export const SERIAL_STRING_LENGTH = 8;

/**
 * Si un valor sirve para grabar como SerialString: exactamente 8 caracteres de
 * "texto seguro" -- ASCII imprimible sin '|', '[' ni ']' (§6.6). Es lo que exige
 * el firmware, que contesta ERR BADDAT a cualquier otra cosa, así que se chequea
 * antes de mandar una trama que la placa va a rechazar.
 *
 * El centinela de "sin grabar" pasa este chequeo -- son ocho guiones, todos
 * imprimibles -- porque acá solo se mira la forma. Distinguirlo es de quien lee.
 */
export const isSerialString = (value) => typeof value === 'string'
  && value.length === SERIAL_STRING_LENGTH
  && /^[ -~]+$/.test(value)
  && !/[|[\]]/.test(value);

/**
 * El serial de hardware que contestó GTSN, o null si la placa no tiene uno.
 *
 * El firmware contesta "00000000" cuando no pudo leerlo ni derivarlo, y eso es
 * el mismo valor en todas las placas a las que les pasa: como identificador no
 * sirve, así que sale de acá como null junto con cualquier cosa que no tenga la
 * forma de 8 hex.
 */
export const parseSerialNumber = (text) => {
  const value = String(text == null ? '' : text).trim().toUpperCase();
  if (!/^[0-9A-F]{8}$/.test(value)) return null;
  return (value === NO_SERIAL_NUMBER) ? null : value;
};

/**
 * La versión que contesta GTFW, tal cual viene (vMAJ.MIN.PATCH).
 *
 * Viaja en el pedido al servidor de seriales y a la consola, y nada más: no se
 * usa para decidir si la placa soporta algo.
 */
export const parseFirmwareVersion = (text) => {
  const value = String(text == null ? '' : text).trim();
  return /^v?\d+\.\d+\.\d+$/.test(value) ? value : null;
};

// ── Bytes ──────────────────────────────────────────────────────────────────────

/**
 * Texto a bytes, para el DATO de STSS / STLB. Solo ASCII: un carácter de más de
 * un byte no es texto seguro y la placa lo rechazaría igual, así que se manda
 * byte por carácter y el chequeo de forma queda para quien llama.
 */
export const encodeText = (text) => Uint8Array.from(String(text), (char) => char.charCodeAt(0) & 0xFF); // eslint-disable-line no-bitwise

// El DATO de los comandos de texto (GTSN, GTSS, GTFW, GTLB y sus ACK).
export const decodeText = (data) => String.fromCharCode(...(data || []));

// Un entero sin signo big-endian de cualquier largo hasta 4 bytes.
export const decodeUint = (data) => {
  if (!data || data.length === 0 || data.length > 4) return null;
  return Array.from(data).reduce((value, byte) => (value * 256) + byte, 0);
};

const toBytes = (data) => {
  if (data == null) return new Uint8Array(0);
  if (typeof data === 'string') return encodeText(data);
  if (typeof data === 'number') return Uint8Array.of(data);
  return Uint8Array.from(data);
};

export const toHex = (data) => Array.from(data || [])
  .map((byte) => byte.toString(16).toUpperCase().padStart(2, '0'))
  .join(' ');

/**
 * XOR de TIPO, ID_HI, ID_LO, CMD, LEN y cada byte de DATO (§4), sin el MAGIC ni
 * el propio CHK. Es lo que calcula calc_checksum() en el firmware.
 */
export const checksum = ({ type, id, cmd, data }) => {
  /* eslint-disable no-bitwise */
  let chk = type ^ (id >> 8) ^ (id & 0xFF) ^ cmd ^ data.length;
  for (let i = 0; i < data.length; i += 1) chk ^= data[i];
  return chk & 0xFF;
  /* eslint-enable no-bitwise */
};

/**
 * El ID liga un REQ con su respuesta. El 0000 está reservado para los eventos que
 * la placa manda por iniciativa propia -- un REQ con ID 0 recibe ERR BADDAT --,
 * así que la numeración va de 0001 a FFFF y vuelve a empezar.
 */
export const nextId = (id) => (id % 0xFFFF) + 1;

/**
 * Arma una trama. DATO puede venir como bytes, como texto (se manda en ASCII) o
 * como un número de un byte (el puerto de GTMD, el contador de ACRS).
 */
export const buildFrame = ({ type, id, cmd, data }) => {
  const bytes = toBytes(data);
  if (bytes.length > MAX_DATA) throw new RangeError(`DATO de ${bytes.length} bytes; el máximo es ${MAX_DATA}`);
  /* eslint-disable no-bitwise */
  const frame = new Uint8Array(8 + bytes.length);
  frame.set([MAGIC0, MAGIC1, type, (id >> 8) & 0xFF, id & 0xFF, cmd, bytes.length]);
  /* eslint-enable no-bitwise */
  frame.set(bytes, 7);
  frame[frame.length - 1] = checksum({ type, id, cmd, data: bytes });
  return frame;
};

/**
 * Lee una trama completa, MAGIC incluido. Devuelve null si no tiene la forma de
 * una; una trama con checksum inválido se devuelve igual, con checksumOk en
 * false: el cliente decide qué hacer con ella, y para diagnosticar sirve más ver
 * qué llegó que un null.
 */
export const parseFrame = (bytes) => {
  if (!bytes || bytes.length < 8 || bytes[0] !== MAGIC0 || bytes[1] !== MAGIC1) return null;
  const len = bytes[6];
  if (len > MAX_DATA || bytes.length !== 8 + len) return null;
  const type = bytes[2];
  const id = (bytes[3] * 256) + bytes[4];
  const cmd = bytes[5];
  const data = Uint8Array.from(bytes.slice(7, 7 + len));
  const chk = bytes[7 + len];
  return {
    type,
    id,
    cmd,
    data,
    chk,
    checksumOk: type <= TYPE.ERR && chk === checksum({ type, id, cmd, data }),
  };
};

export const isEvent = (frame) => !!frame && frame.type === TYPE.EVT;

// ── Payloads ───────────────────────────────────────────────────────────────────

/**
 * Una lectura de módulo: el DATO de GTMD y EVMD (§6.4), 1 byte de puerto + 2 de
 * milivoltios big-endian. La placa ya no dice qué módulo es: eso sale de
 * moduleTable, con la tabla de la placa que corresponda.
 */
export const parseModuleData = (data) => {
  if (!data || data.length !== 3) return null;
  return { port: data[0], mv: (data[1] * 256) + data[2] };
};

// El DATO de EVBT: 1 byte de ResetKind + 4 de uptime previo en segundos.
export const parseBootData = (data) => {
  if (!data || data.length < 1) return { kind: 'UNKN', previousUptime: 0 };
  return {
    kind: RESET_KIND[data[0]] || 'UNKN',
    previousUptime: data.length === 5 ? decodeUint(data.slice(1, 5)) : 0,
  };
};

// El DATO de GTFL y EVFL: el código de fault, o null si es "sin fault".
export const parseFault = (data) => {
  if (!data || data.length !== 1 || data[0] === FAULT_NONE) return null;
  return FAULT[data[0]] || `?${data[0]}`;
};

const TEXT_COMMANDS = [CMD.GTSN, CMD.GTSS, CMD.GTFW, CMD.GTLB, CMD.STLB, CMD.STSS];

/**
 * Una línea legible para la consola, como la escribe el host de Python:
 * "RSP|0001|GTSN|55ADD4AE", "EVT|0000|EVMD|P1:420mV". Lo que no se sabe leer va en
 * hex.
 */
export const describeFrame = (frame) => {
  const { type, id, cmd, data } = frame;
  const head = `${typeName(type)}|${id.toString(16).toUpperCase().padStart(4, '0')}|${cmdName(cmd)}`;
  let shown = '----';
  if (data.length > 0) {
    const module = (cmd === CMD.GTMD || cmd === CMD.EVMD) && type !== TYPE.REQ ? parseModuleData(data) : null;
    if (TEXT_COMMANDS.includes(cmd)) shown = decodeText(data);
    else if (module) shown = `P${module.port}:${module.mv}mV`;
    else shown = toHex(data);
  }
  return `${head}|${shown}`;
};

// ── Stream ─────────────────────────────────────────────────────────────────────

/**
 * Separa el stream del puerto en tramas del protocolo y texto del usuario (§3).
 *
 * Esto es lo que mantiene el monitor limpio: todo lo que no es una trama es del
 * sketch del usuario y va al monitor tal cual, y las tramas son nuestras y no se
 * le muestran.
 *
 * Trabaja byte a byte, como el firmware, y guarda el estado entre llamadas porque
 * una trama puede llegar partida en varios chunks -- el buffer de la UART no
 * respeta límites de trama --, incluso entre los dos bytes del MAGIC.
 *
 * El texto se decodifica como UTF-8 en modo stream, recién acá y solo lo que es
 * texto: decodificar el chunk entero antes de separarlo convertía el MAGIC en
 * caracteres de reemplazo y no se veía ninguna trama. La secuencia 0xAA 0xF5 no
 * se forma con texto UTF-8 válido -- 0xF5 nunca aparece en él --, así que no hay
 * tilde, ñ ni ° que la confunda.
 */
export class StreamSplitter {
  constructor() {
    this.reset();
  }

  reset() {
    this.decoder = new TextDecoder();
    this.afterMagic0 = false;
    // Lo que va de la trama en curso, sin el MAGIC; null fuera de una trama.
    this.frame = null;
  }

  /**
   * Procesa un chunk y devuelve las tramas completas que cerró y el texto del
   * usuario que pasó por el medio.
   */
  push(chunk) {
    const frames = [];
    const textBytes = [];
    const bytes = chunk || [];
    for (let i = 0; i < bytes.length; i += 1) {
      const byte = bytes[i];
      if (this.frame) {
        this.frameByte(byte, frames);
      } else if (this.afterMagic0) {
        this.afterMagic0 = false;
        if (byte === MAGIC1) {
          this.frame = [];
        } else {
          // El 0xAA era texto: un byte de continuación de UTF-8, por ejemplo.
          textBytes.push(MAGIC0);
          if (byte === MAGIC0) this.afterMagic0 = true;
          else textBytes.push(byte);
        }
      } else if (byte === MAGIC0) {
        this.afterMagic0 = true;
      } else {
        textBytes.push(byte);
      }
    }
    const text = textBytes.length > 0 ? this.decoder.decode(Uint8Array.from(textBytes), { stream: true }) : '';
    return { frames, text };
  }

  frameByte(byte, frames) {
    const frame = this.frame;
    frame.push(byte);
    // TIPO, ID_HI, ID_LO, CMD, LEN: con LEN ya se sabe si la trama es posible.
    if (frame.length === 5 && frame[4] > MAX_DATA) {
      /**
       * Un largo imposible: lo que parecía un inicio de trama no lo era. Se
       * descarta y se vuelve a buscar el MAGIC, como hace la placa.
       */
      this.frame = null;
      return;
    }
    if (frame.length >= 6 && frame.length === 6 + frame[4]) {
      this.frame = null;
      const parsed = parseFrame(Uint8Array.from([MAGIC0, MAGIC1, ...frame]));
      if (parsed) frames.push(parsed);
    }
  }
}
